import { useEffect, useState, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from '../components/Sidebar';
import { BottomBar } from '../components/BottomBar';
import MasterErrorBar from '../components/MasterErrorBar';
import { useDatabase } from "../contexts/DatabaseContext";
import { useAuth } from "../contexts/AuthContext";
import { useAuthGet } from "../hooks/useAxios";
import RegisterWorkspace from '../components/RegisterWorkspace';
import { catchErrorParse } from "../ErrorUtility";
import Visualizer from "../visualizer/Visualizer";
// https://react-icons.github.io/react-icons/icons/ri/

export default function DashboardLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { clientData, clientLoading, clientBad, createInitializationEvent } = useDatabase();

    const [cardData, setCardData] = useState(null);
    const [invoiceData, setInvoiceData] = useState(null);
    const [balance, setBalance] = useState(null);
    const cardDataLoading = useRef(false);
    const invoiceDataLoading = useRef(false);
    const balanceLoading = useRef(false);

    const [isWorkspaceRegisterOpen, setIsWorkspaceRegisterOpen] = useState(false);
    const [skipTransition, setSkipTransition] = useState(false);

    const [mobile, setMobile] = useState(window.innerWidth <= 775 || window.innerHeight <= 600);
    const [portrait, setPortrait] = useState(window.innerHeight > window.innerWidth);

    const { url, currentUser } = useAuth();
    const { get } = useAuthGet();

    const [showVisualizer, setShowVisualizer] = useState(false);
    const [visualizerConfig, setVisualizerConfig] = useState(null);
    const requestedVisualTrigger = useRef(null);
    const requestedVisualIndex = useRef(null);

    async function getCardData(showError = true) {
        if (clientLoading || clientBad || !clientData || !clientData.stripeCustomerId) return;
        if (cardDataLoading.current) return;
        cardDataLoading.current = true;
        const [responseData, error, errorCode, errorMessage] = await catchErrorParse(get, `${url}/webapp/payment-methods/${clientData.stripeCustomerId}`);
        if (error && showError) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                        code: errorCode,
                        message: errorMessage ? errorMessage : error.message || "Failed to load card data."
                    }
            }));
        }
        setCardData(responseData);
        cardDataLoading.current = false;
    }

    async function getInvoiceData(showError = true) {
        if (clientLoading || clientBad || !clientData || !clientData.stripeCustomerId) return;
        if (invoiceDataLoading.current) return;
        invoiceDataLoading.current = true;
        const [responseData, error, errorCode, errorMessage] = await catchErrorParse(get, `${url}/webapp/invoices/${clientData.stripeCustomerId}`);
        if (error && showError) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    code: errorCode,
                    message: errorMessage ? errorMessage : error.message || "Failed to load invoice data."
                }
            }));
        }
        setInvoiceData(responseData);
        invoiceDataLoading.current = false;
    }

    async function getBalance(showError = true) {
        if (clientLoading || clientBad || !clientData || !clientData.stripeCustomerId) return;
        if (balanceLoading.current) return;
        balanceLoading.current = true;
        const [responseData, error, errorCode, errorMessage] = await catchErrorParse(get, `${url}/webapp/balance/${clientData.stripeCustomerId}`);
        if (error && showError) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    code: errorCode,
                    message: errorMessage ? errorMessage : error.message || "Failed to load balance."
                }
            }));
        }
        setBalance(responseData?.balance / 100);
        balanceLoading.current = false;
    }

    useEffect(() => {
        if (clientBad) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    message: "Failed to load client data."
                }
            }));
        }
    }, [clientBad]); 

    useEffect(() => {
        if (location.state?.openWorkspaceRegister) {
            setSkipTransition(true);
            setIsWorkspaceRegisterOpen(true);
            
            navigate(location.pathname, { replace: true, state: {} });
            
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setSkipTransition(false);
                });
            });
        }
    }, [location]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerHeight > window.innerWidth) {
                setPortrait(true);
            } else {
                setPortrait(false);
            }
            if (window.innerWidth <= 775 || window.innerHeight <= 600) {
                setMobile(true);
            } else {
                setMobile(false);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Set initial width

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const outletContext = {
        clientData,
        clientDataLoading: clientLoading,
        cardData: cardData?.data,
        getCardData,
        isWorkspaceRegisterOpen,
        setIsWorkspaceRegisterOpen,
        invoiceData: invoiceData?.data || [],
        getInvoiceData,
        mobile,
        portrait,
        balance,
        getBalance,
        setVisualizerConfig,
        requestedVisualTrigger,
        requestedVisualIndex,
    }

    useEffect(() => {
        if (clientData?.failedInvoice && (location?.pathname !== '/auth/invoice-failed' && location?.pathname !== '/auth/resubscribe')) {
            navigate('/auth/invoice-failed', { replace: true });
        }
    }, [clientData?.failedInvoice]);

    useEffect(() => {
        if (clientLoading) {
            setShowVisualizer(false);
        } else {
            setTimeout(() => {
                setShowVisualizer(true);
                createInitializationEvent();
            }, 1000);
        }
    }, [clientLoading]);

    useEffect(() => {
        if (isWorkspaceRegisterOpen && !cardData && clientData?.stripeCustomerId) {
            console.log("Getting card data");
            getCardData();
        }
    }, [isWorkspaceRegisterOpen, cardData, clientData?.stripeCustomerId]);

    if (clientData?.failedInvoice) {
        return (
            <main className="main-wrapper">
                <MasterErrorBar />
                <Visualizer activeConfig={visualizerConfig} requestedVisualTrigger={requestedVisualTrigger} requestedVisualIndex={requestedVisualIndex} currentUser={currentUser} />
                <Outlet context={outletContext} />
            </main>
        );
    }

    function handleResetVisualizerConfig() {
        if (visualizerConfig) {
            setVisualizerConfig(null);
        }
    }

    return (
        <main>
            <div onClick={handleResetVisualizerConfig}>
                {portrait ? (
                    <BottomBar
                        isWorkspaceRegisterOpen={isWorkspaceRegisterOpen}
                        setIsWorkspaceRegisterOpen={setIsWorkspaceRegisterOpen}
                        mobile={mobile}
                    />
                    
                ) : (
                    <Sidebar
                        isWorkspaceRegisterOpen={isWorkspaceRegisterOpen}
                        setIsWorkspaceRegisterOpen={setIsWorkspaceRegisterOpen}
                        mobile={mobile}
                    />
                )}
            </div>
            <MasterErrorBar />
            {showVisualizer && <Visualizer activeConfig={visualizerConfig} requestedVisualTrigger={requestedVisualTrigger} requestedVisualIndex={requestedVisualIndex} currentUser={currentUser} />}
            <div className={`register-workspace-overlay 
                ${isWorkspaceRegisterOpen ? 'open' : ''} 
                ${skipTransition ? 'no-transition' : ''}`}>
                <RegisterWorkspace 
                    clientData={clientData}
                    clientDataLoading={clientLoading}
                    setIsWorkspaceRegisterOpen={setIsWorkspaceRegisterOpen}
                />
            </div>
            <div className="container-fluid">
                <div className="main-container">
                    <Outlet context={outletContext} />
                </div>
            </div>
            {/* This can be uncommented when we want to test the master error bar */}
            {/* {process.env.NODE_ENV === 'development' && (
                <button 
                    onClick={generateDebugError}
                    style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        zIndex: 9999,
                        padding: '8px 16px',
                        backgroundColor: '#ff4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Generate Error
                </button>
            )} */}
        </main>
    );
}
